/**
 * Owner Dashboard (AB#2206 redesign, founder-approved 2026-11-05).
 *
 * Today first: what needs the owner, today's appointments with the one next
 * action the backend allows, and a compact "today at a glance"; period
 * performance sits below the fold (default last 30 days). Every figure comes
 * from an existing endpoint — see useOwnerToday and useDashboard.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import { useBusinessDetailsQuery } from '../../../hooks/useBusinessDetails';
import { useDashboard } from '../../../hooks/useDashboard';
import { queryKeys } from '../../../lib/queryKeys';
import { fill, localizedName } from '../../../lib/staffDisplay';
import { formatDurationMinutes } from '../../../lib/humanDuration';
import BookingDetailDialog from '../../../components/BookingDetail/BookingDetailDialog';
import { generateIdempotencyKey, listBookingModel } from '../../../components/BookingDetail/staffBooking';
import MarkAsPaidModal from '../../../components/MarkAsPaidModal/MarkAsPaidModal';
import QuickBookModal from '../../../components/QuickBookModal/QuickBookModal';
import TrialBanner from '../../../components/TrialBanner/TrialBanner';
// AB#1904 (W-1) — one-time post-upgrade welcome banner (sessionStorage one-shot).
import PostUpgradeBanner from '../../../components/PostUpgradeBanner/PostUpgradeBanner';
import { useCurrentSubscription, subscriptionStatusToNumber } from '../../../hooks/useCurrentSubscription';
import type { SalonOwnerAppointmentRowDto } from '../../../services/appointmentsList';
import type { BranchDto } from '../../../types/api-responses';
import { buildRoster, nextAppointment, summarizeToday } from './dashboardModel';
import { formatLongDay } from './dashboardFormat';
import { useActiveBranches, useMinuteClock, useOwnerToday } from './useOwnerToday';
import { AttentionStrip, TodayAppointments, TodayGlance } from './TodaySection';
import { PerformanceSection, type PerfRange } from './PerformanceSection';
import s from './Dashboard.module.css';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useTranslation = useTranslationUntyped as any;

/** "+ New booking": straight into Quick book for one branch, or ask which branch first. */
const NewBookingButton: React.FC<{ branches: BranchDto[]; language: string }> = ({ branches, language }) => {
    const { t } = useLanguage();
    const [menuOpen, setMenuOpen] = useState(false);
    const [branch, setBranch] = useState<BranchDto | null>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const closeMenu = () => { setMenuOpen(false); triggerRef.current?.focus(); };

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [menuOpen]);

    const single = branches.length === 1 ? branches[0] : null;
    return (
        <div className={s.newBooking} ref={wrapRef}>
            <button
                ref={triggerRef}
                type="button"
                className={s.btnPrimary}
                disabled={branches.length === 0}
                aria-expanded={single ? undefined : menuOpen}
                aria-controls={single ? undefined : 'dash-new-booking-branches'}
                onClick={() => (single ? setBranch(single) : setMenuOpen(o => !o))}
                data-testid="dash-new-booking"
            >
                <span aria-hidden="true">+</span> {t('odash.newBooking')}
            </button>
            {menuOpen && (
                <div
                    id="dash-new-booking-branches"
                    className={s.menu}
                    role="group"
                    aria-labelledby="dash-new-booking-branches-title"
                    onKeyDown={e => { if (e.key === 'Escape') closeMenu(); }}
                    onBlur={e => {
                        // Tabbing out of the chooser closes it.
                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setMenuOpen(false);
                    }}
                >
                    <p id="dash-new-booking-branches-title" className={s.menuTitle}>{t('odash.newBooking.pickBranch')}</p>
                    {branches.map((b, i) => (
                        <button
                            key={b.id}
                            type="button"
                            className={s.menuItem}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus={i === 0}
                            onClick={() => { setMenuOpen(false); setBranch(b); }}
                        >
                            {localizedName(b.name, b.nameAr, language)}
                        </button>
                    ))}
                </div>
            )}
            {branch && (
                <QuickBookModal
                    branchId={branch.id}
                    branchName={localizedName(branch.name, branch.nameAr, language)}
                    onClose={() => setBranch(null)}
                />
            )}
        </div>
    );
};

const Dashboard: React.FC = () => {
    const { t, language } = useLanguage();
    const { showToast } = useToast();
    // The mark-paid toast copy lives in the react-i18next 'appointments' namespace (same as the Appointments list).
    const { t: tAppt } = useTranslation('appointments');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const now = useMinuteClock();

    // AB#1982 (AC-3) — tenant currency from the business profile; AED fallback, never '$'.
    const { data: business } = useBusinessDetailsQuery();
    const currency = business?.currencyIso ?? 'AED';
    const salonName = business?.name ?? '';

    const { branches, isLoading: branchesLoading } = useActiveBranches();
    const [branchId, setBranchId] = useState<number | null>(null);
    const scopeIds = useMemo(() => (branchId != null ? [branchId] : []), [branchId]);
    const branchLabel = (b: { name: string; nameAr?: string | null }) => localizedName(b.name, b.nameAr, language);

    const today = useOwnerToday(scopeIds, branches, now);
    const summary = useMemo(() => summarizeToday(today.rows), [today.rows]);
    const next = useMemo(() => nextAppointment(today.rows, today.nowHHmm), [today.rows, today.nowHHmm]);
    const roster = useMemo(
        () => buildRoster(today.grids.map(g => ({ branchName: branchLabel(g.branch), grid: g.grid })), today.rows),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [today.grids, today.rows, language],
    );
    const todayStaff = useMemo(
        () => new Set(today.rows.filter(r => r.staffId != null).map(r => r.staffId)).size,
        [today.rows],
    );

    const perf = useDashboard({ range: 'last30days' });
    const perfRange: PerfRange = perf.range === 'last7days' || perf.range === 'thismonth' ? perf.range : 'last30days';

    // PBI-C — trial / past-due banner when relevant.
    const { data: subscription } = useCurrentSubscription();
    const subscriptionStatusNum = subscriptionStatusToNumber(subscription?.status);

    const [detail, setDetail] = useState<SalonOwnerAppointmentRowDto | null>(null);
    const [payRow, setPayRow] = useState<SalonOwnerAppointmentRowDto | null>(null);
    const idempotencyKeyRef = useRef('');
    const openMarkPaid = (row: SalonOwnerAppointmentRowDto) => {
        idempotencyKeyRef.current = generateIdempotencyKey();
        setDetail(null);
        setPayRow(row);
    };
    // Booking actions refresh the appointments + calendar caches themselves; the
    // period figures are not cached, so reload them after a change too.
    const onChanged = () => { void perf.refresh(); };

    const minutesAgo = today.todayUpdatedAt ? Math.max(0, Math.floor((now.getTime() - today.todayUpdatedAt) / 60_000)) : null;
    const meta = [
        salonName,
        // Never "0 branches" while the list is still loading.
        branchesLoading ? null : branches.length === 1 ? t('odash.meta.branchOne') : fill(t('odash.meta.branches'), { count: branches.length }),
        perf.hasLoaded ? fill(t('odash.meta.staff'), { count: perf.summary.employees }) : null,
        minutesAgo == null ? null : minutesAgo < 1 ? t('odash.meta.updatedNow') : fill(t('odash.meta.updatedAgo'), { wait: formatDurationMinutes(minutesAgo, t) }),
    ].filter(Boolean);

    const branchQuery = branchId != null ? `&branchId=${branchId}` : '';
    const calendarHref = `/calendar?day=${today.today}${branchId != null ? `&branchId=${branchId}` : ''}`;
    const scopeBranches = branchId != null ? branches.filter(b => b.id === branchId) : branches;

    return (
        <div className={s.page}>
            <PostUpgradeBanner />
            {subscription && subscriptionStatusNum > 0 && (
                <TrialBanner
                    status={subscriptionStatusNum}
                    trialEndAt={subscription.trialEndAt}
                    planName={subscription.planName}
                    isFoundingMember={subscription.isFoundingMember}
                />
            )}

            <header className={s.header}>
                <div className={s.headerText}>
                    <p className={s.eyebrow}>{t('odash.eyebrow')}</p>
                    <h1 className={s.title} data-testid="dash-date">{formatLongDay(today.today, language)}</h1>
                    <p className={s.meta}>{meta.join(' · ')}</p>
                </div>
                <div className={s.headerActions}>
                    {branches.length > 1 && (
                        <label className={s.select}>
                            <span className={s.srOnly}>{t('odash.branch.label')}</span>
                            <select
                                id="dash-branch"
                                value={branchId ?? ''}
                                onChange={e => setBranchId(e.target.value ? Number(e.target.value) : null)}
                                data-testid="dash-branch"
                            >
                                <option value="">{t('odash.branch.all')}</option>
                                {branches.map(b => <option key={b.id} value={b.id}>{branchLabel(b)}</option>)}
                            </select>
                        </label>
                    )}
                    <NewBookingButton branches={scopeBranches} language={language} />
                </div>
            </header>

            <AttentionStrip
                loading={today.todayLoading || today.pending.loading || today.unpaid.loading}
                pendingCount={today.pending.count}
                pendingEarliest={today.pending.earliest}
                unpaidCount={today.unpaid.count}
                unpaidAmount={today.unpaid.amount}
                unpaidPartial={today.unpaid.amountIsPartial}
                currency={today.unpaid.currency ?? currency}
                next={next}
                nowHHmm={today.nowHHmm}
                branchQuery={branchQuery}
                onOpen={setDetail}
            />

            <div className={s.todayGrid}>
                <TodayAppointments
                    rows={today.rows}
                    hasMore={today.todayHasMore}
                    loading={today.todayLoading}
                    error={today.todayError}
                    onRetry={() => { void today.refetchToday(); }}
                    nowHHmm={today.nowHHmm}
                    staffCount={todayStaff}
                    calendarHref={calendarHref}
                    branchQuery={branchQuery}
                    onOpen={setDetail}
                    onMarkPaid={openMarkPaid}
                    onChanged={onChanged}
                />
                <TodayGlance
                    summary={summary}
                    currency={today.rows[0]?.currencyIso ?? currency}
                    loading={today.todayLoading}
                    roster={roster}
                    // Until branches load there are no grid queries yet — that is loading, not "nobody on shift".
                    rosterLoading={branchesLoading || today.rosterLoading}
                    rosterError={today.rosterError}
                    multiBranch={scopeBranches.length > 1}
                />
            </div>

            <PerformanceSection
                range={perfRange}
                onRange={r => perf.setRange(r)}
                loaded={perf.hasLoaded}
                error={!!perf.error && !perf.loading}
                onRetry={() => { void perf.refresh(); }}
                currency={currency}
                totalAppointments={perf.summary.totalAppointments}
                revenue={perf.totalPaymentRevenue}
                labels={perf.bookingsRevenue.labels}
                bookings={perf.bookingsRevenue.bookings}
                amounts={perf.bookingsRevenue.amounts}
                statLabels={perf.appointmentStats.labels}
                statValues={perf.appointmentStats.values}
                recentPayments={perf.recentPayments}
                breakdown={perf.paymentMethodBreakdown}
                branchSelected={branchId != null}
            />

            {detail && (
                <BookingDetailDialog
                    booking={listBookingModel(detail)}
                    onClose={() => setDetail(null)}
                    onMarkPaid={() => openMarkPaid(detail)}
                    footerAction={(
                        <button
                            type="button"
                            className={s.btnSecondary}
                            onClick={() => navigate(`/calendar?day=${detail.appointmentDate.slice(0, 10)}&branchId=${detail.branchId}&apptId=${detail.id}&from=list`)}
                        >
                            {t('booking.action.openCalendar')}
                        </button>
                    )}
                />
            )}

            {payRow && (
                <MarkAsPaidModal
                    appointmentId={payRow.id}
                    customer={{ name: payRow.clientDisplayName, phone: payRow.clientPhoneFull }}
                    service={{ name: payRow.serviceName, amount: payRow.amount, currency: payRow.currencyIso }}
                    staff={{ name: payRow.staffDisplayName, branch: payRow.branchName }}
                    isOpen
                    onClose={() => setPayRow(null)}
                    onSuccess={() => {
                        showToast(tAppt('markPaidModal.success'), 'success');
                        setPayRow(null);
                        void queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });
                        void perf.refresh();
                    }}
                    idempotencyKey={idempotencyKeyRef.current}
                    source="list"
                    bundle={payRow.bundleBookingId != null ? { name: payRow.bundleName } : undefined}
                />
            )}
        </div>
    );
};

export default Dashboard;
